import React, { useEffect, useRef } from 'react';

/* =========================================================
   PREMIUM 3D GOLD FOIL CONFETTI (MATCHES SCREENSHOT DENSITY)
   ========================================================= */
function GoldConfettiCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;

    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);

      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();

    window.addEventListener('resize', resize);

    /* =====================================================
       PREMIUM METALLIC GOLD SHADES (3D SHINE GRADIENTS)
       ===================================================== */
    const goldPalettes = [
      { c1: '#FFF8C7', c2: '#F4CC64', c3: '#8A5A00' }, // Ultra Bright Gold
      { c1: '#FFE270', c2: '#D7A63A', c3: '#6B4706' }, // Classic Metallic Gold
      { c1: '#FFFFFF', c2: '#E1A92B', c3: '#94661D' }, // Specular Foil Shine
      { c1: '#FFFBEB', c2: '#FACC15', c3: '#A16207' }, // Vivid Yellow Gold
    ];

    /* High density particle count to match dense screenshot look */
    const particleCount = Math.min(
      16600,
      Math.max(700, Math.floor((width * height) / 1000))
    );

    const particles = Array.from({ length: particleCount }).map(() => {
      const typeRandom = Math.random();

      let type = 'trapezoid';

      if (typeRandom < 0.25) {
        type = 'disc'; // Metallic circular coins/discs
      } else if (typeRandom < 0.55) {
        type = 'trapezoid'; // Angled foil flakes
      } else if (typeRandom < 0.8) {
        type = 'sliver'; // Thin long foil strips
      } else {
        type = 'dust'; // Tiny glowing background specks
      }

      const palette =
        goldPalettes[Math.floor(Math.random() * goldPalettes.length)];

      return {
        x: Math.random() * width,
        y: Math.random() * height - height,

        width:
          type === 'sliver'
            ? Math.random() * 3.5 + 2
            : type === 'disc'
            ? Math.random() * 8 + 5
            : Math.random() * 10 + 6,

        height:
          type === 'sliver'
            ? Math.random() * 18 + 10
            : type === 'disc'
            ? Math.random() * 8 + 5
            : Math.random() * 12 + 6,

        color: palette,

        speedY: Math.random() * 2.2 + 0.9,

        speedX: Math.random() * 1.2 - 0.6,

        rotation: Math.random() * Math.PI * 2,

        rotationSpeed: (Math.random() - 0.5) * 0.08,

        /* 3D Flip Flutter simulation */
        flip: Math.random() * Math.PI * 2,
        flipSpeed: Math.random() * 0.08 + 0.03,

        swayAmount: Math.random() * 1.8 + 0.4,

        swaySpeed: Math.random() * 0.02 + 0.008,

        phase: Math.random() * Math.PI * 2,

        opacity: Math.random() * 0.35 + 0.65,

        type,
      };
    });

    let time = 0;

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      time += 1;

      particles.forEach((p) => {
        /* Movement Physics */
        p.y += p.speedY;

        p.x +=
          p.speedX +
          Math.sin(time * p.swaySpeed + p.phase) * p.swayAmount * 0.4;

        p.rotation += p.rotationSpeed;
        p.flip += p.flipSpeed;

        /* Screen Reset */
        if (p.y > height + 30) {
          p.y = -20 - Math.random() * 40;
          p.x = Math.random() * width;
        }

        if (p.x < -30) p.x = width + 20;
        if (p.x > width + 30) p.x = -20;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);

        /* 3D Fluttering Flip Effect */
        const flipScale = Math.cos(p.flip);
        ctx.scale(1, Math.abs(flipScale) < 0.1 ? 0.1 : flipScale);

        ctx.globalAlpha = p.opacity;

        /* Create Dynamic Metallic Gradient */
        const grad = ctx.createLinearGradient(
          -p.width / 2,
          -p.height / 2,
          p.width / 2,
          p.height / 2
        );
        grad.addColorStop(0, p.color.c1);
        grad.addColorStop(0.5, p.color.c2);
        grad.addColorStop(1, p.color.c3);

        ctx.fillStyle = grad;

        /* -------------------------------------------------
           1. 3D GOLD DISC / CIRCLE
           ------------------------------------------------- */
        if (p.type === 'disc') {
          ctx.beginPath();
          ctx.arc(0, 0, p.width / 2, 0, Math.PI * 2);
          ctx.fill();

          /* Metallic Rim Highlight */
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 0.6;
          ctx.globalAlpha = p.opacity * 0.5;
          ctx.stroke();

          ctx.restore();
          return;
        }

        /* -------------------------------------------------
           2. DUST SPECKS
           ------------------------------------------------- */
        if (p.type === 'dust') {
          ctx.beginPath();
          ctx.arc(0, 0, Math.random() * 1.5 + 1, 0, Math.PI * 2);
          ctx.fill();

          ctx.restore();
          return;
        }

        /* -------------------------------------------------
           3. THIN METALLIC SLIVER / STRIP
           ------------------------------------------------- */
        if (p.type === 'sliver') {
          ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);

          ctx.restore();
          return;
        }

        /* -------------------------------------------------
           4. ANGLED FOIL TRAPEZOID / SHRED
           ------------------------------------------------- */
        ctx.beginPath();
        ctx.moveTo(-p.width * 0.5, -p.height * 0.4);
        ctx.lineTo(p.width * 0.4, -p.height * 0.6);
        ctx.lineTo(p.width * 0.5, p.height * 0.5);
        ctx.lineTo(-p.width * 0.3, p.height * 0.4);
        ctx.closePath();
        ctx.fill();

        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 199,
        display: 'block',
      }}
    />
  );
}

/* =========================================================
   CONGRATULATIONS MODAL
   ========================================================= */

export default function Congratulations({
  showCongrats,
  result,
  winningCode,
  onClose,
}) {
  if (!showCongrats || !result) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px',
        backdropFilter: 'blur(4px)',
        background: 'rgba(0, 0, 0, 0.42)',
        overflowY: 'auto',
        boxSizing: 'border-box',
      }}
    >
      {/* Dense 3D Metallic Gold Confetti */}
      <GoldConfettiCanvas />

      {/* Backdrop Click */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 200,
        }}
        onClick={onClose}
      />

      {/* Card */}
      <div
        style={{
          position: 'relative',
          zIndex: 201,
          width: '100%',
          maxWidth: '360px',
          background: 'linear-gradient(180deg, #1C1711 0%, #17130F 100%)',
          border: '1px solid #D6B156',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow:
            '0 20px 50px rgba(0,0,0,0.9), 0 0 25px rgba(214,177,86,0.10)',
          animation: 'congratsPopIn 0.42s ease-out forwards',
          fontFamily: "'Century Gothic', Arial, sans-serif",
          boxSizing: 'border-box',
        }}
      >
        <style>
          {`
            @keyframes congratsPopIn {
              from {
                opacity: 0;
                transform: scale(0.72) translateY(25px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }

            @keyframes buttonShine {
              0% { left: -120%; }
              45%, 100% { left: 120%; }
            }

            @keyframes topGlow {
              0%, 100% {
                opacity: 0.55;
                transform: scaleX(0.82);
              }
              50% {
                opacity: 1;
                transform: scaleX(1);
              }
            }

            @keyframes bottomGlow {
              0%, 100% {
                opacity: 0.45;
                transform: scaleX(0.78);
              }
              50% {
                opacity: 0.95;
                transform: scaleX(1);
              }
            }
          `}
        </style>

        {/* Header */}
        <div
          style={{
            padding: '18px 16px 17px 16px',
            textAlign: 'center',
            borderBottom: '1px solid #E0A113',
            boxSizing: 'border-box',
          }}
        >
          <h2
            style={{
              margin: 0,
              color: '#FFFFFF',
              fontFamily:
                '"Brush Script MT", "Segoe Script", "Lucida Handwriting", cursive',
              fontSize: 'clamp(48px, 8vw, 34px)',
              fontWeight: 500,
              lineHeight: 1,
              letterSpacing: '0.2px',
              textShadow: '0 2px 4px rgba(0,0,0,0.7)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            <span>Congratulations!</span>
            <span
              style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: 'clamp(20px, 6vw, 28px)',
                lineHeight: 1,
                transform: 'translateY(-1px)',
              }}
            >
              🎉
            </span>
          </h2>
        </div>

        {/* Main Result Area */}
        <div
          style={{
            padding: '24px 18px 24px 18px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              color: '#D6B156',
              fontSize: 'clamp(10px, 2.8vw, 12px)',
              letterSpacing: '4px',
              textTransform: 'uppercase',
              marginBottom: '7px',
              fontWeight: 'bold',
            }}
          >
            You won
          </div>

          {/* 1. Pehle Space ke baad wala text show ho (e.g. 1 pkr) */}
          {result.rewardText && (
            <div
              style={{
                color: '#FFF0B0',
                fontSize: 'clamp(20px, 6vw, 26px)',
                fontWeight: 800,
                marginTop: '0px',
                marginBottom: '10px',
                letterSpacing: '0.5px',
                lineHeight: 1.2,
                maxWidth: '100%',
                overflowWrap: 'anywhere',
                textAlign: 'center',
              }}
            >
              {result.rewardText}
            </div>
          )}

          {/* 2. Phir 'ON' */}
          <div
            style={{
              color: '#D6B156',
              fontSize: 'clamp(9px, 2.5vw, 11px)',
              letterSpacing: '4px',
              textTransform: 'uppercase',
              marginBottom: '14px',
              fontWeight: 'bold',
            }}
          >
           DISCOUNT ON
          </div>

          {/* 3. Phir Space se pehle wala main label show ho (e.g. perfume) */}
          <div
            style={{
              border: '1px solid #D6B156',
              borderRadius: '6px',
              padding: '9px 20px',
              color: '#FFFFFF',
              fontSize: 'clamp(11px, 3.2vw, 15px)',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              marginBottom: '24px',
              fontWeight: 'bold',
              maxWidth: '100%',
              boxSizing: 'border-box',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {result.label || result.subCategory || 'ACCESSORIES'}
          </div>
          

          {/* WhatsApp Badge */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '7px',
              border: '1px solid rgba(255,255,255,0.8)',
              borderRadius: '20px',
              padding: '6px 12px',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '12px',
                height: '12px',
                minWidth: '12px',
                background: '#D6B156',
                borderRadius: '2px',
              }}
            >
              <span
                style={{
                  color: '#181511',
                  fontSize: '8px',
                  fontWeight: '900',
                  lineHeight: 1,
                }}
              >
                ✓
              </span>
            </div>

            <div
              style={{
                color: '#D6B156',
                fontSize: 'clamp(7px, 2vw, 9px)',
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}
            >
              Code sent to your WhatsApp number
            </div>
          </div>
        </div>

        {/* Bottom Claim Area */}
        <div
          style={{
            padding: '20px 18px 18px 18px',
            borderTop: '1px solid #E0A113',
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          <button
            onClick={onClose}
            type="button"
            style={{
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              minHeight: 'clamp(48px, 13vw, 58px)',
              padding: '0 clamp(16px, 4vw, 22px)',
              borderRadius: '30px',
              border: '1px solid #FDE599',
              background:
                'linear-gradient(90deg, #FFF3C1 0%, #ce9130 20%, #eeddb0 42%, #ce9130 68%, #FFE69A 100%)',
              color: '#181511',
              fontWeight: 'bold',
              fontSize: 'clamp(14px, 4vw, 18px)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'clamp(6px, 2vw, 10px)',
              boxShadow:
                '0 0 20px rgba(214,177,86,0.65), inset 0 0 10px rgba(255,255,255,0.65)',
              fontFamily: "'Century Gothic', Arial, sans-serif",
              boxSizing: 'border-box',
              outline: 'none',
            }}
          >
            {/* Top Shine */}
            <div
              style={{
                position: 'absolute',
                top: '-1px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '68%',
                height: '4px',
                borderRadius: '50%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), rgba(255,250,210,1), rgba(255,255,255,0.95), transparent)',
                filter: 'blur(1.5px)',
                boxShadow: '0 0 10px 2px rgba(255,238,158,0.85)',
                animation: 'topGlow 1.8s ease-in-out infinite',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />

            {/* Bottom Glow */}
            <div
              style={{
                position: 'absolute',
                bottom: '-1px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '72%',
                height: '4px',
                borderRadius: '50%',
                background:
                  'linear-gradient(90deg, transparent, #ffffffbf, rgba(255,224,125,1), rgba(255,255,255,0.75), transparent)',
                filter: 'blur(1.6px)',
                boxShadow: '0 0 10px 2px rgba(255,205,70,0.65)',
                animation: 'bottomGlow 2.1s ease-in-out infinite',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />

            {/* Moving Light Beam */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: '-120%',
                width: '90%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255,255,255,0.16), rgba(255,255,255,0.70), rgba(255,255,255,0.16), transparent)',
                transform: 'skewX(-20deg)',
                animation: 'buttonShine 2.8s infinite',
                pointerEvents: 'none',
                zIndex: 0,
              }}
            />

           {/* Solid Black Gift SVG Icon */}
<span
  style={{
    fontSize: "20px",
    position: "relative",
    zIndex: 1,
    display: "inline-block", // Animation transform ke liye zaroori hai
    animation: "giftBounce 1.2s ease-in-out infinite"
  }}
>
  🎁
</span>
            

            <span
              style={{
                position: 'relative',
                zIndex: 2,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              LET's Go
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}